import React, { PropsWithChildren, useContext, useMemo } from "react";

import InstrumentationRules from "../rules/InstrumentationRules";
import AssetRules from "../rules/AssetRules";
import NodeRules from "../rules/NodeRules";
import { ApplicationRulesFactory } from "../rules/ApplicationRules";
import { SystemRules } from "../rules";

type rulesShape = {
  instrumentation: $TSFixMeFunction;
  asset: $TSFixMeFunction;
  node: $TSFixMeFunction;
  application: $TSFixMeFunction;
  system: $TSFixMeFunction;
};

type WithRulesProps = {
  rules: rulesShape;
};

const RulesContext = React.createContext<WithRulesProps | null>(null);

type RulesContextProviderProps = {
  testRules?: $TSFixMe;
};

function RulesContextProvider({
  testRules = {},
  children,
}: Readonly<PropsWithChildren<RulesContextProviderProps>>) {
  const applicationRules = new ApplicationRulesFactory();

  /* istanbul ignore next */
  const createSystemRules = (system: $TSFixMe, assets: $TSFixMe) =>
    new SystemRules(system, assets);

  /* istanbul ignore next */
  const createInstrumentationRules = (
    instrumentation: $TSFixMe,
    assets: $TSFixMe,
  ) => new InstrumentationRules(instrumentation, assets);

  /* istanbul ignore next */
  const createAssetRules = (asset: $TSFixMe) => new AssetRules(asset);

  /* istanbul ignore next */
  const createNodeRules = (node: $TSFixMe) => new NodeRules(node);

  /* istanbul ignore next */
  const createApplicationRules = () => applicationRules;

  const context = useMemo(
    () => ({
      rules: {
        system: createSystemRules,
        node: createNodeRules,
        instrumentation: createInstrumentationRules,
        asset: createAssetRules,
        application: createApplicationRules,
        ...testRules,
      },
    }),
    [],
  );

  return (
    <RulesContext.Provider value={context}>{children}</RulesContext.Provider>
  );
}

const withRules = <T extends WithRulesProps = WithRulesProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(props: Readonly<Omit<T, keyof WithRulesProps>>) {
    return (
      <RulesContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withRules must be used inside a RulesContextProvider",
            );
          }
          return <ChildComponent {...(props as T)} {...context} />;
        }}
      </RulesContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

const useRules = () => {
  const context = useContext(RulesContext);
  // istanbul ignore next
  if (!context) {
    throw new Error("useRules must be used inside a RulesContextProvider");
  }
  return context.rules;
};

export { type rulesShape, RulesContextProvider, withRules, useRules };
